import React, { useState } from "react";

import {
    Box, Checkbox,
    FormControl, FormControlLabel, FormHelperText,
    InputLabel, MenuItem, OutlinedInput,
    Radio, RadioGroup,
    Select, TextField, Typography
} from "@mui/material";
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import Markdown from "react-markdown";
import { formatDate } from "../../../utilities/formatters";
import dayjs from "dayjs";

import { childIsRequired, childIsVisible } from "./formFunctions";

const formatOptions = (options) => {
    if (!options) {
        return [];
    }
    if (Array.isArray(options)) {
        return options;
    }
    return [
        {
            label: options?.label || "",
            value: options?.value || "",
        },
    ];
};

const TitleBuilder = ({ title }) => {
    if (!title?.length > 0) return null;
    return (
        <Typography variant="h4" component="h3" mb={1}>
            {title}
        </Typography>
    );
};

const DescriptionBuilder = ({ description, type, isRequired }) => {
    description = description.replace(/\[\/?strong\]/g, '**');
    return (
        <Typography component="p" mb={2}>
            <Markdown>{description}</Markdown>
            {["radio", "date", "checkbox"].includes(type) && isRequired && (
                <span className="error"> *</span>
            )}
        </Typography>
    );
};

const ElementBuilder = ({
    element,
    canUpdate,
    formId,
    onFieldChange,
    parentValue = null,
}) => {
    const [response, setResponse] = useState(element?.response || "");
    const [checked, setChecked] = useState(
        !!element && element.value === element.response
    );

    if (!childIsVisible(element.displayCondition, parentValue)) {
        return null;
    }

    const handleChange = (event) => setResponse(event.target.value);

    const updateValue = (value) => {
        if (value !== element.response) {
            if (element.type === "date") {
                value = formatDate(value);
            }
            const params = {
                id: formId,
                elements: [
                    {
                        id: element.id,
                        name: element.name,
                        response: value,
                    },
                ],
            };
            onFieldChange(params);
        }
    };

    const isRequired =
        !!element.isRequired ||
        childIsRequired(element.isRequiredCondition, parentValue);
    const disabled =
        !canUpdate ||
        (element.hasOwnProperty("canUpdate") && !element.canUpdate);

    const name = element?.name || "element";
    const value = element?.value || "";
    const options = formatOptions(element?.options);

    let label = element?.label || "Enter text here";
    const labelId = `${formId}_label`;
    let placeholder = "Enter text here";
    const helperTextId = `${formId}_helper-text`;

    let fullWidth = true;
    let hiddenLabel = false;
    let renderedElement = "";

    switch (element.type) {
        case "text":
            label = isRequired ? "Required" : disabled ? "" : label;
            renderedElement = (
                <OutlinedInput
                    id={name}
                    aria-labelledby={labelId}
                    aria-describedby={helperTextId}
                    value={response}
                    onChange={handleChange}
                    onBlur={({ target }) => updateValue(target.value)}
                    disabled={disabled}
                    placeholder={disabled ? "" : placeholder}
                />
            );
            break;

        case "textarea":
            if (isRequired) {
                label = "Required";
            }

            renderedElement = (
                <OutlinedInput
                    id={name}
                    aria-labelledby={labelId}
                    aria-describedby={helperTextId}
                    value={response}
                    onChange={handleChange}
                    onBlur={({ target }) => updateValue(target.value)}
                    disabled={disabled}
                    placeholder={disabled ? "" : placeholder}
                    multiline
                    minRows={2}
                />
            );
            break;

        case "checkbox":
            hiddenLabel = true;
            renderedElement = (
                <FormControlLabel
                    aria-labelledby={labelId}
                    aria-describedby={helperTextId}
                    label={label}
                    control={
                        <Checkbox
                            id={name}
                            disabled={disabled}
                            value={value}
                            checked={checked}
                            onChange={({ target }) => {
                                setChecked(target.checked);
                                updateValue(
                                    target.checked ? element.value : null
                                );
                            }}
                        />
                    }
                />
            );
            break;

        case "select":
            label = "Select";
            fullWidth = false;
            renderedElement = (
                <Select
                    id={name}
                    labelId={labelId}
                    aria-describedby={helperTextId}
                    value={response}
                    onChange={(event) => {
                        handleChange(event);
                        updateValue(event.target.value);
                    }}
                    disabled={disabled}
                >
                    {options.map((option) => (
                        <MenuItem value={option.value} key={option.label}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            );
            break;

        case "select-multiple":
            label = "Select";
            renderedElement = (
                <Select
                    id={name}
                    labelId={labelId}
                    aria-describedby={helperTextId}
                    multiple
                    value={response || []}
                    renderValue={(selected) =>
                        options
                            .filter(({ id }) => selected.includes(id))
                            .map(({ label }) => label)
                            .join(", ")
                    }
                    onChange={(event) => handleChange(event)}
                    onClose={() => updateValue(response)}
                    disabled={disabled}
                >
                    {options.map((option) => (
                        <MenuItem value={option.id} key={option.label}>
                            <FormControlLabel
                                label={option.label}
                                control={
                                    <Checkbox
                                        id={option.label}
                                        checked={response?.includes(option.id)}
                                    />
                                }
                            />
                        </MenuItem>
                    ))}
                </Select>
            );
            break;

        case "radio":
            hiddenLabel = true;
            renderedElement = (
                <RadioGroup
                    row
                    name={name}
                    aria-labelledby={labelId}
                    aria-describedby={helperTextId}
                    defaultValue={response}
                    onChange={({ target }) => {
                        setResponse(target.value);
                        updateValue(target.value);
                    }}
                >
                    {options.map((option) => (
                        <FormControlLabel
                            value={option.value}
                            control={<Radio disabled={disabled} />}
                            label={option.label}
                            key={option.label}
                        />
                    ))}
                </RadioGroup>
            );
            break;

        case "date":
            fullWidth = false;
            hiddenLabel = true;
            label = disabled ? "" : "Date";
            if (isRequired) {
                label = "Required";
            }
            renderedElement = (
                <DatePicker
                    id={name}
                    aria-labelledby={labelId}
                    aria-describedby={helperTextId}
                    label={label}
                    format="DD/MM/YYYY"
                    value={dayjs(response)}
                    onChange={(value) => {
                        setResponse(value);
                        updateValue(value);
                    }}
                    renderInput={(params) => <TextField {...params} />}
                    disabled={disabled}
                />
            );
            break;

        case "datetime-local":
            fullWidth = false;
            hiddenLabel = true;
            label = disabled ? "" : "Date time";
            if (isRequired) {
                label = "Required";
            }
            renderedElement = (
                <DateTimePicker
                    id={name}
                    aria-labelledby={labelId}
                    aria-describedby={helperTextId}
                    label={label}
                    format="DD/MM hh:mm"
                    value={dayjs(response)}
                    onChange={(value) => {
                        setResponse(value);
                        updateValue(value);
                    }}
                    renderInput={(props) => <TextField {...props} />}
                />
            );
            break;

        case "file":
            hiddenLabel = true;
            break;

        default:
            hiddenLabel = true;
            break;
    }

    const childElements = !element?.children
        ? null
        : element.children.map((child) => (
              <ElementBuilder
                  element={child}
                  formId={formId}
                  canUpdate={canUpdate}
                  onFieldChange={onFieldChange}
                  key={child.id}
                  parentValue={response}
              />
          ));

    return (
        <Box component="div" className="form-row">
            <TitleBuilder title={element.title} />
            <DescriptionBuilder description={element.description} type={element.type} isRequired={isRequired} />
            <FormControl
                component="div"
                fullWidth={fullWidth}
                required={isRequired}
                error={isRequired && !response}
                hiddenLabel={hiddenLabel}
                style={{ minWidth: 120 }}
            >
                {!hiddenLabel && (
                    <InputLabel
                        id={labelId}
                        htmlFor={name}
                        sx={{ backgroundColor: "#FFF", padding: "0 4px" }}
                    >
                        {label}
                    </InputLabel>
                )}
                {renderedElement}
                {childElements}
                <FormHelperText id={helperTextId}>
                    {element?.helperText || ""}
                </FormHelperText>
            </FormControl>
        </Box>
    );
};

const FormBuilder = ({ form, canUpdate, onFieldChange }) => {
    if (!form?.elements?.length) {
        return null;
    }

    return (
        <Box
            component="form"
            autoComplete="off"
            className="card-content"
            noValidate
        >
            {form.elements
                .sort((a, b) => a.id - b.id)
                .map((element) => (
                    <ElementBuilder
                        element={element}
                        canUpdate={!!canUpdate}
                        formId={form.id}
                        key={element.id}
                        onFieldChange={onFieldChange}
                        parentValue={element?.response}
                    />
                ))}
        </Box>
    );
};

export default FormBuilder;
